import { useState, useEffect } from "react";
import { supabase } from "../../lib/supabase";
import { Facebook, Instagram, AlertCircle, Save, TestTube, RefreshCw, Eye, EyeOff, ChevronDown, ChevronUp, Search, Filter, BarChart3, Lock, Unlock, Shield, ShieldOff } from "lucide-react";
import toast from "react-hot-toast";

interface SocialAccount {
  id: string;
  platform: string;
  page_name: string;
  page_id?: string;
  ig_business_id?: string;
  instagram_account_id?: string;
  instagram_direct_equivalent_id?: string;
  connection_type?: string;
}

interface AutoDMSettings {
  id?: string;
  social_account_id: string;
  enabled: boolean;
  trigger_types: string[];
  mode: "only_if_no_previous_conversation" | "always";
  conversation_lookback_hours: number;
  cooldown_minutes: number;
  webhook_url: string;
  api_key?: string;
  direct_connection_id?: string;
  send_channel?: string;
}

interface LogEntry {
  id: string;
  created_at: string;
  event_type: string;
  platform: string;
  external_username?: string;
  status: string;
  http_code?: number;
  error_message?: string;
}

interface AccountStats {
  total_attempts: number;
  success_count: number;
  error_count: number;
  permanent_failure_count: number;
  last_success?: string;
  last_error?: string;
}

interface ResponseHistory {
  response_id?: string;
  has_response: boolean;
  external_user_id: string;
  external_username?: string;
  interaction_type: string;
  ai_response_text?: string;
  delivery_status?: string;
  response_sent_at?: string;
  meta_message_id?: string;
  response_error_message?: string;
  response_created_at?: string;
  log_id?: string;
  trigger_status?: string;
  trigger_http_code?: number;
  trigger_error_message?: string;
  trigger_webhook_url?: string;
  trigger_created_at: string;
  social_account_id: string;
  platform: string;
}

const TRIGGER_TYPES = [
  { value: "comment_created", label: "Komentarze pod postami" },
  { value: "comment_replied", label: "Odpowiedzi na komentarze" },
  { value: "like_added", label: "Polubienia" },
  { value: "reaction_added", label: "Reakcje" },
  { value: "mention", label: "Wzmianki" },
  { value: "story_mention", label: "Wzmianki w Stories" },
  { value: "page_follow", label: "Nowe obserwacje (Facebook)" },
  { value: "instagram_follow", label: "Nowe obserwacje (Instagram)" },
];

interface ConversationLock {
  id: string;
  external_user_id: string;
  conversation_id?: string;
  locked: boolean;
  locked_by_user_id?: string;
  locked_by_name?: string;
  locked_at: string;
  lock_source: string;
  unlocked_at?: string;
  created_at: string;
}

export default function InteractionAutoDM() {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [settings, setSettings] = useState<Record<string, AutoDMSettings>>({});
  const [logs, setLogs] = useState<Record<string, LogEntry[]>>({});
  const [stats, setStats] = useState<Record<string, AccountStats>>({});
  const [responses, setResponses] = useState<Record<string, ResponseHistory[]>>({});
  const [conversationLocks, setConversationLocks] = useState<Record<string, ConversationLock[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [expandedAccount, setExpandedAccount] = useState<string | null>(null);
  const [showLogs, setShowLogs] = useState<string | null>(null);
  const [showResponses, setShowResponses] = useState<string | null>(null);
  const [showLocks, setShowLocks] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterEnabled, setFilterEnabled] = useState<"all" | "enabled" | "disabled">("all");
  const [filterPlatform, setFilterPlatform] = useState<"all" | "facebook" | "instagram">("all");
  const [userSubscriptionTier, setUserSubscriptionTier] = useState<string | null>(null);
  const [showPremiumModal, setShowPremiumModal] = useState(false);
  const [unlocking, setUnlocking] = useState<string | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      setLoading(true);

      // Get user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Check user subscription tier
      const { data: userData } = await supabase
        .from("users")
        .select("subscription_tier")
        .eq("id", user.id)
        .single();

      setUserSubscriptionTier(userData?.subscription_tier || "free");

      // If not premium/vip, show modal and block access
      if (!userData?.subscription_tier || !["premium", "vip"].includes(userData.subscription_tier)) {
        setShowPremiumModal(true);
        setLoading(false);
        return;
      }

      // Load social accounts (Facebook and Instagram Direct only - hide Instagram Business)
      const { data: accountsData, error: accountsError } = await supabase
        .from("social_accounts")
        .select("*")
        .eq("owner_user_id", user.id)
        .in("platform", ["facebook", "instagram_direct"])
        .eq("status", "active")
        .order("platform", { ascending: true })
        .order("page_name", { ascending: true });

      if (accountsError) throw accountsError;

      // Only show Instagram Direct accounts that are connected to Instagram Business
      const filteredAccounts = (accountsData || []).filter((acc) => {
        if (acc.platform === "facebook") return true;
        if (acc.platform === "instagram_direct") {
          // Show only if linked to a Business account
          return acc.linked_instagram_business_id != null;
        }
        return false;
      });

      setAccounts(filteredAccounts);

      // Load existing settings (including for linked Instagram Business accounts)
      const { data: settingsData, error: settingsError } = await supabase
        .from("interaction_auto_dm_settings")
        .select("*")
        .eq("user_id", user.id);

      if (settingsError) throw settingsError;

      const settingsMap: Record<string, AutoDMSettings> = {};
      (settingsData || []).forEach((s) => {
        settingsMap[s.social_account_id] = s;
      });

      // For Instagram Direct accounts, ensure we display their own settings (not Business)
      // But keep Business settings in map for loading logs
      setSettings(settingsMap);

      // Load recent logs and stats for each account - IN PARALLEL
      const logsMap: Record<string, LogEntry[]> = {};
      const statsMap: Record<string, AccountStats> = {};

      // Create all log fetch promises in parallel
      const logPromises = filteredAccounts.map(async (account) => {
        const allLogs: LogEntry[] = [];

        // Load logs for this account
        const accountSettings = settingsMap[account.id];
        if (accountSettings?.id) {
          const { data: logsData } = await supabase
            .from("interaction_auto_dm_logs")
            .select("*")
            .eq("settings_id", accountSettings.id)
            .order("created_at", { ascending: false })
            .limit(20);

          if (logsData) {
            allLogs.push(...logsData);
          }
        }

        // If Instagram Direct, also load logs from linked Instagram Business
        if (account.platform === "instagram_direct" && account.linked_instagram_business_id) {
          const businessSettings = settingsMap[account.linked_instagram_business_id];
          if (businessSettings?.id) {
            const { data: businessLogsData } = await supabase
              .from("interaction_auto_dm_logs")
              .select("*")
              .eq("settings_id", businessSettings.id)
              .order("created_at", { ascending: false })
              .limit(20);

            if (businessLogsData) {
              allLogs.push(...businessLogsData);
            }
          }
        }

        // Sort combined logs by date and limit to 20 most recent
        if (allLogs.length > 0) {
          const sortedLogs = allLogs
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
            .slice(0, 20);

          logsMap[account.id] = sortedLogs;

          // Calculate stats from combined logs
          const totalAttempts = sortedLogs.length;
          const successCount = sortedLogs.filter(l => l.status === "success").length;
          const errorCount = sortedLogs.filter(l => l.status === "error").length;
          const permanentFailureCount = sortedLogs.filter(l => l.status === "permanent_failure").length;
          const lastSuccess = sortedLogs.find(l => l.status === "success")?.created_at;
          const lastError = sortedLogs.find(l => l.status === "error" || l.status === "permanent_failure")?.created_at;

          statsMap[account.id] = {
            total_attempts: totalAttempts,
            success_count: successCount,
            error_count: errorCount,
            permanent_failure_count: permanentFailureCount,
            last_success: lastSuccess,
            last_error: lastError,
          };
        }

        return { accountId: account.id };
      });

      // Wait for all log fetches to complete
      await Promise.all(logPromises);

      setLogs(logsMap);
      setStats(statsMap);

      // Load complete interaction history (triggers + responses) for each account - IN PARALLEL
      const responsesMap: Record<string, ResponseHistory[]> = {};

      const historyPromises = filteredAccounts.map(async (account) => {
        const { data: completeHistory } = await supabase.rpc(
          "get_interaction_auto_dm_complete_history",
          {
            p_social_account_id: account.id,
            p_include_linked_account: account.platform === "instagram_direct",
            p_limit: 50,
          }
        );

        if (completeHistory && completeHistory.length > 0) {
          responsesMap[account.id] = completeHistory;
        }

        return { accountId: account.id };
      });

      // Wait for all history fetches to complete
      await Promise.all(historyPromises);

      setResponses(responsesMap);

      // Load conversation locks for each account - IN PARALLEL
      const locksMap: Record<string, ConversationLock[]> = {};

      const lockPromises = filteredAccounts.map(async (account) => {
        const { data: locks } = await supabase.rpc(
          "get_conversation_locks_for_account",
          {
            p_social_account_id: account.id,
            p_limit: 50,
          }
        );

        if (locks && locks.length > 0) {
          locksMap[account.id] = locks;
        }

        return { accountId: account.id };
      });

      await Promise.all(lockPromises);
      setConversationLocks(locksMap);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Błąd ładowania danych");
    } finally {
      setLoading(false);
    }
  }

  async function saveSettings(accountId: string) {
    try {
      setSaving(accountId);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const accountSettings = settings[accountId];
      if (!accountSettings) throw new Error("No settings found");

      // Validate required fields
      if (accountSettings.enabled) {
        if (!accountSettings.webhook_url || accountSettings.webhook_url.trim() === "") {
          toast.error("URL webhooka jest wymagany");
          return;
        }

        if (accountSettings.trigger_types.length === 0) {
          toast.error("Wybierz przynajmniej jeden typ interakcji");
          return;
        }
      }

      // Generate signing secret if not exists
      let apiKey = accountSettings.api_key;
      if (!apiKey) {
        const { data: secretData, error: secretError } = await supabase.rpc("generate_webhook_signing_secret");
        if (secretError) throw secretError;
        apiKey = secretData;
      }

      // Determine send channel and direct connection ID for Instagram
      const account = accounts.find((a) => a.id === accountId);
      let sendChannel = "facebook_messenger";
      let directConnectionId = null;

      if (account?.platform === "instagram") {
        sendChannel = "instagram_direct";
        // Try to find Instagram Direct equivalent
        if (account.instagram_direct_equivalent_id) {
          directConnectionId = account.instagram_direct_equivalent_id;
        }
      } else if (account?.platform === "instagram_direct") {
        sendChannel = "instagram_direct";
        directConnectionId = accountId;
      }

      const dataToSave = {
        user_id: user.id,
        social_account_id: accountId,
        enabled: accountSettings.enabled,
        trigger_types: accountSettings.trigger_types,
        mode: accountSettings.mode,
        conversation_lookback_hours: accountSettings.conversation_lookback_hours,
        cooldown_minutes: accountSettings.cooldown_minutes ?? 15,
        webhook_url: accountSettings.webhook_url,
        api_key: apiKey,
        direct_connection_id: directConnectionId,
        send_channel: sendChannel,
      };

      const { data, error } = await supabase
        .from("interaction_auto_dm_settings")
        .upsert(dataToSave, { onConflict: "social_account_id" })
        .select()
        .single();

      if (error) throw error;

      setSettings((prev) => ({
        ...prev,
        [accountId]: { ...dataToSave, id: data.id },
      }));

      // If this is Instagram Direct, automatically sync settings to linked Instagram Business
      if (account?.platform === "instagram_direct" && account.linked_instagram_business_id) {
        try {
          const businessDataToSave = {
            user_id: user.id,
            social_account_id: account.linked_instagram_business_id,
            enabled: accountSettings.enabled,
            trigger_types: accountSettings.trigger_types,
            mode: accountSettings.mode,
            conversation_lookback_hours: accountSettings.conversation_lookback_hours,
            cooldown_minutes: accountSettings.cooldown_minutes ?? 15,
            webhook_url: accountSettings.webhook_url,
            api_key: apiKey,
            direct_connection_id: accountId, // Point to the Direct account
            send_channel: "instagram_direct",
          };

          await supabase
            .from("interaction_auto_dm_settings")
            .upsert(businessDataToSave, { onConflict: "social_account_id" });

          console.log("✅ Synced settings to linked Instagram Business account");
        } catch (syncError) {
          console.error("Failed to sync to Instagram Business:", syncError);
          // Don't block the main save
        }
      }

      toast.success("Ustawienia zapisane");
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error("Błąd zapisu ustawień");
    } finally {
      setSaving(null);
    }
  }

  async function testWebhook(accountId: string) {
    try {
      setTesting(accountId);

      const accountSettings = settings[accountId];
      if (!accountSettings?.webhook_url) {
        toast.error("Najpierw zapisz URL webhooka");
        return;
      }

      if (!accountSettings?.id) {
        toast.error("Najpierw zapisz ustawienia konta (kliknij Zapisz)");
        return;
      }

      const account = accounts.find((a) => a.id === accountId);
      if (!account) return;

      const testPayload = {
        social_account_id: accountId,
        event_type: "comment_created",
        platform: account.platform === "instagram_direct" ? "instagram" : account.platform,
        external_user_id: "test_user_12345",
        external_username: "test_user",
        content_id: "test_post_12345",
        comment_id: "test_comment_12345",
        interaction_data: { test: true },
      };

      const { data, error } = await supabase.functions.invoke("interaction-webhook-trigger", {
        body: testPayload,
      });

      if (error) {
        console.error("Test webhook error:", error);
        toast.error("Błąd funkcji: " + error.message);
        return;
      }

      if (data?.skipped || !data?.triggered) {
        toast("Test webhook pominięty: " + (data.reason || "Webhook not triggered"), {
          icon: "ℹ️",
          duration: 5000
        });
      } else if (data?.triggered) {
        const webhookResponse = data.webhook_response;
        let parsedResponse;

        try {
          parsedResponse = typeof webhookResponse === 'string' ? JSON.parse(webhookResponse) : webhookResponse;
        } catch (e) {
          parsedResponse = webhookResponse;
        }

        if (data.webhook_status === 200 && parsedResponse) {
          let responseText = "";

          if (Array.isArray(parsedResponse) && parsedResponse[0]?.message?.text) {
            responseText = parsedResponse[0].message.text;
          } else if (parsedResponse.message?.text) {
            responseText = parsedResponse.message.text;
          }

          if (responseText) {
            toast.success(
              <div className="max-w-md">
                <div className="font-semibold mb-2">Test webhook pomyślny!</div>
                <div className="text-sm bg-blue-50 p-2 rounded border border-blue-200 mt-2">
                  <div className="font-medium text-blue-900 mb-1">Odpowiedź AI:</div>
                  <div className="text-blue-800 whitespace-pre-wrap">{responseText}</div>
                </div>
              </div>,
              { duration: 8000 }
            );
          } else {
            toast.success("Test webhook wysłany pomyślnie! HTTP " + data.webhook_status);
          }
        } else {
          toast.error(`Test webhook zwrócił status ${data.webhook_status}`, {
            duration: 6000
          });
        }
      } else {
        toast.error("Test webhook nieudany: " + (data?.error || "Unknown error"), {
          duration: 6000
        });
      }

      // Reload logs
      await loadData();
    } catch (error) {
      console.error("Error testing webhook:", error);
      toast.error("Błąd testowania webhooka: " + (error instanceof Error ? error.message : "Unknown"));
    } finally {
      setTesting(null);
    }
  }

  function updateSetting(accountId: string, field: keyof AutoDMSettings, value: any) {
    setSettings((prev) => ({
      ...prev,
      [accountId]: {
        ...(prev[accountId] || {
          social_account_id: accountId,
          enabled: false,
          trigger_types: [],
          mode: "only_if_no_previous_conversation",
          conversation_lookback_hours: 0,
          cooldown_minutes: 15,
          webhook_url: "",
        }),
        [field]: value,
      },
    }));
  }

  async function unlockConversation(accountId: string, externalUserId: string) {
    try {
      setUnlocking(externalUserId);

      const { data, error } = await supabase.rpc("unlock_conversation", {
        p_social_account_id: accountId,
        p_external_user_id: externalUserId,
      });

      if (error) throw error;

      if (data) {
        toast.success("Konwersacja odblokowana – bot może ponownie odpowiadać");

        // Update local state
        setConversationLocks((prev) => ({
          ...prev,
          [accountId]: (prev[accountId] || []).map((lock) =>
            lock.external_user_id === externalUserId
              ? { ...lock, locked: false, unlocked_at: new Date().toISOString() }
              : lock
          ),
        }));
      } else {
        toast("Konwersacja nie była zablokowana", { icon: "ℹ️" });
      }
    } catch (error) {
      console.error("Error unlocking conversation:", error);
      toast.error("Błąd odblokowania konwersacji");
    } finally {
      setUnlocking(null);
    }
  }

  function toggleTriggerType(accountId: string, triggerType: string) {
    const currentSettings = settings[accountId];
    const currentTypes = currentSettings?.trigger_types || [];

    if (currentTypes.includes(triggerType)) {
      updateSetting(
        accountId,
        "trigger_types",
        currentTypes.filter((t) => t !== triggerType)
      );
    } else {
      updateSetting(accountId, "trigger_types", [...currentTypes, triggerType]);
    }
  }

  function getPlatformIcon(platform: string) {
    if (platform === "facebook") return <Facebook className="w-5 h-5 text-blue-600" />;
    return <Instagram className="w-5 h-5 text-pink-600" />;
  }

  function getPlatformName(platform: string) {
    if (platform === "facebook") return "Facebook Page";
    if (platform === "instagram_direct") return "Instagram Direct (DM)";
    return "Instagram Business";
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Premium Modal
  if (showPremiumModal) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Funkcja Premium
            </h2>

            <p className="text-gray-600 mb-6">
              Auto DM po Interakcji jest dostępne tylko dla użytkowników z pakietami <span className="font-semibold text-purple-600">Premium</span> i <span className="font-semibold text-pink-600">VIP</span>.
            </p>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 mb-2">
                Twój aktualny pakiet: <span className="font-semibold capitalize">{userSubscriptionTier || "Free"}</span>
              </p>
              <p className="text-xs text-gray-600">
                Automatyczne odpowiedzi DM na komentarze, wzmianki i reakcje
              </p>
            </div>

            <a
              href="https://aiagenthub.pl"
              target="_blank"
              rel="noopener noreferrer"
              className="block w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition-all mb-3"
            >
              Przejdź do zakupu pakietu
            </a>

            <button
              onClick={() => window.location.reload()}
              className="w-full text-gray-600 py-2 px-6 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Odśwież stronę
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (accounts.length === 0) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Brak połączonych kont Instagram Direct lub Facebook</p>
        <p className="text-sm text-gray-500 mt-2">
          Aby używać Auto DM dla Instagram, musisz połączyć Instagram Business z Instagram Direct w zakładce "Konta społecznościowe"
        </p>
      </div>
    );
  }

  // Filter and sort accounts
  const filteredAccounts = accounts
    .filter((account) => {
      // Search filter
      if (searchTerm && !account.page_name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }

      // Enabled filter
      const isEnabled = settings[account.id]?.enabled || false;
      if (filterEnabled === "enabled" && !isEnabled) return false;
      if (filterEnabled === "disabled" && isEnabled) return false;

      // Platform filter
      if (filterPlatform === "facebook" && account.platform !== "facebook") return false;
      if (filterPlatform === "instagram" && !["instagram", "instagram_direct"].includes(account.platform)) {
        return false;
      }

      return true;
    })
    .sort((a, b) => {
      // Sort enabled first
      const aEnabled = settings[a.id]?.enabled || false;
      const bEnabled = settings[b.id]?.enabled || false;
      if (aEnabled && !bEnabled) return -1;
      if (!aEnabled && bEnabled) return 1;

      // Then by platform
      if (a.platform < b.platform) return -1;
      if (a.platform > b.platform) return 1;

      // Then by name
      return a.page_name.localeCompare(b.page_name);
    });

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-medium mb-2">Automatyczna wiadomość po interakcji - Zgodne z Meta API</p>
            <p className="text-blue-700 mb-2">
              Gdy ktoś wejdzie w interakcję z Twoim kontem (komentarz, like, reakcja), system automatycznie wywoła
              zewnętrzny webhook (np. n8n). Wymaga planu PREMIUM lub VIP.
            </p>
            <div className="bg-blue-100 rounded p-3 text-blue-800 text-xs space-y-1">
              <p><strong>Instagram:</strong> Wyświetlamy tylko konta Instagram Direct, które są połączone z Instagram Business. Ustawienia są automatycznie synchronizowane.</p>
              <p><strong>Zgodność z Meta:</strong> System respektuje politykę Meta API - DM można wysłać tylko po interakcji użytkownika.</p>
              <p><strong>Limit prób:</strong> Max 3 próby na interakcję. Po wykryciu braku zgody użytkownika (permanent failure) kolejne próby są blokowane.</p>
              <p><strong>Historia odpowiedzi:</strong> N8N powinien odsyłać wygenerowane odpowiedzi AI z powrotem do systemu przez callback webhook. Zobacz dokumentację.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Szukaj konta..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <select
              value={filterEnabled}
              onChange={(e) => setFilterEnabled(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Wszystkie</option>
              <option value="enabled">Włączone</option>
              <option value="disabled">Wyłączone</option>
            </select>

            <select
              value={filterPlatform}
              onChange={(e) => setFilterPlatform(e.target.value as any)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Wszystkie platformy</option>
              <option value="facebook">Facebook</option>
              <option value="instagram">Instagram</option>
            </select>
          </div>

          <div className="text-sm text-gray-600">
            Znaleziono: <span className="font-medium">{filteredAccounts.length}</span> z {accounts.length}
          </div>
        </div>
      </div>

      {filteredAccounts.length === 0 && (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Brak kont pasujących do filtrów</p>
          <button
            onClick={() => {
              setSearchTerm("");
              setFilterEnabled("all");
              setFilterPlatform("all");
            }}
            className="mt-4 text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Wyczyść filtry
          </button>
        </div>
      )}

      {filteredAccounts.map((account) => {
        const accountSettings = settings[account.id];
        const isEnabled = accountSettings?.enabled || false;
        const isExpanded = expandedAccount === account.id;
        const accountLogs = logs[account.id] || [];
        const accountStats = stats[account.id];
        const accountLocks = conversationLocks[account.id] || [];
        const activeLocks = accountLocks.filter((l) => l.locked).length;

        return (
          <div key={account.id} className={`bg-white rounded-lg shadow-sm border-2 transition-colors ${isEnabled ? 'border-blue-500' : 'border-gray-200'}`}>
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3 flex-1">
                  {getPlatformIcon(account.platform)}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-gray-900">{account.page_name}</h3>
                      {activeLocks > 0 && (
                        <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                          <Lock className="w-3 h-3" />
                          {activeLocks} zablokowanych
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{getPlatformName(account.platform)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isEnabled}
                      onChange={(e) => {
                        updateSetting(account.id, "enabled", e.target.checked);
                        if (e.target.checked && !isExpanded) {
                          setExpandedAccount(account.id);
                        }
                      }}
                      className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Włączone</span>
                  </label>

                  <button
                    onClick={() => setExpandedAccount(isExpanded ? null : account.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Stats - show only if enabled */}
              {isEnabled && accountStats && (
                <div className="grid grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <BarChart3 className="w-4 h-4 text-gray-400" />
                      <p className="text-2xl font-bold text-gray-900">{accountStats.total_attempts}</p>
                    </div>
                    <p className="text-xs text-gray-600">Wszystkie próby</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{accountStats.success_count}</p>
                    <p className="text-xs text-gray-600">Sukces</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-600">{accountStats.error_count}</p>
                    <p className="text-xs text-gray-600">Błąd</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-orange-600">{accountStats.permanent_failure_count}</p>
                    <p className="text-xs text-gray-600">Brak zgody</p>
                  </div>
                </div>
              )}

              {isExpanded && (
                <div className="space-y-4 mt-6 pt-6 border-t border-gray-200">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Typy interakcji *</label>
                    <div className="grid grid-cols-2 gap-2">
                      {TRIGGER_TYPES.map((trigger) => (
                        <label key={trigger.value} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(accountSettings?.trigger_types || []).includes(trigger.value)}
                            onChange={() => toggleTriggerType(account.id, trigger.value)}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="text-sm text-gray-700">{trigger.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Warunek konwersacji</label>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`mode-${account.id}`}
                          value="only_if_no_previous_conversation"
                          checked={
                            (accountSettings?.mode || "only_if_no_previous_conversation") ===
                            "only_if_no_previous_conversation"
                          }
                          onChange={() => updateSetting(account.id, "mode", "only_if_no_previous_conversation")}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Tylko gdy brak wcześniejszej konwersacji</span>
                      </label>

                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name={`mode-${account.id}`}
                          value="always"
                          checked={accountSettings?.mode === "always"}
                          onChange={() => updateSetting(account.id, "mode", "always")}
                          className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Zawsze wysyłaj (nawet gdy konwersacja istnieje)</span>
                      </label>
                    </div>
                  </div>

                  {accountSettings?.mode === "only_if_no_previous_conversation" && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sprawdzaj konwersacje z ostatnich (godzin)
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={accountSettings.conversation_lookback_hours || 0}
                        onChange={(e) =>
                          updateSetting(account.id, "conversation_lookback_hours", parseInt(e.target.value) || 0)
                        }
                        className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <p className="text-xs text-gray-500 mt-1">0 = sprawdź wszystkie konwersacje w historii</p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Cooldown między wiadomościami (minuty)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="1440"
                      value={accountSettings?.cooldown_minutes ?? 15}
                      onChange={(e) =>
                        updateSetting(account.id, "cooldown_minutes", parseInt(e.target.value) || 0)
                      }
                      className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Minimalna przerwa między kolejnymi Auto DM do tej samej osoby.<br />
                      <span className="font-medium">Domyślnie: 15 minut</span> (zapobiega spamowaniu)<br />
                      <span className="text-gray-400">0 = wyłączone (wysyłaj zawsze)</span>
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">URL webhooka *</label>
                    <input
                      type="url"
                      value={accountSettings?.webhook_url || ""}
                      onChange={(e) => updateSetting(account.id, "webhook_url", e.target.value)}
                      placeholder="https://your-n8n.app/webhook/..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Wyślemy POST request z danymi interakcji do tego URL
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      API Key
                      <span className="text-xs text-gray-500 ml-2">(użyj w header: Authorization: Bearer [key])</span>
                    </label>
                    <div className="flex gap-2">
                      <input
                        type={showSecret[account.id] ? "text" : "password"}
                        value={accountSettings?.api_key || ""}
                        onChange={(e) =>
                          setSettings((prev) => ({
                            ...prev,
                            [account.id]: {
                              ...prev[account.id],
                              api_key: e.target.value,
                            },
                          }))
                        }
                        placeholder="Zostaw puste aby wygenerować automatycznie"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret((prev) => ({ ...prev, [account.id]: !prev[account.id] }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      >
                        {showSecret[account.id] ? (
                          <EyeOff className="w-4 h-4 text-gray-600" />
                        ) : (
                          <Eye className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Możesz ustawić własny klucz lub pozostawić puste - system wygeneruje bezpieczny klucz automatycznie
                    </p>
                  </div>

                  <div className="flex gap-3 pt-4">
                    <button
                      onClick={() => saveSettings(account.id)}
                      disabled={saving === account.id}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving === account.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Zapisz
                    </button>

                    <button
                      onClick={() => testWebhook(account.id)}
                      disabled={testing === account.id || !accountSettings?.webhook_url || !accountSettings?.id}
                      className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      title={!accountSettings?.id ? "Najpierw zapisz ustawienia" : "Wyślij testowy webhook"}
                    >
                      {testing === account.id ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <TestTube className="w-4 h-4" />
                      )}
                      Test
                    </button>

                    {accountLogs.length > 0 && (
                      <button
                        onClick={() => setShowLogs(showLogs === account.id ? null : account.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                      >
                        <Eye className="w-4 h-4" />
                        Logi ({accountLogs.length})
                      </button>
                    )}

                    {(responses[account.id]?.length || 0) > 0 && (
                      <button
                        onClick={() => setShowResponses(showResponses === account.id ? null : account.id)}
                        className="flex items-center gap-2 px-4 py-2 border border-green-300 bg-green-50 text-green-700 rounded-lg hover:bg-green-100"
                      >
                        <BarChart3 className="w-4 h-4" />
                        Historia ({responses[account.id].length})
                      </button>
                    )}

                    {accountLocks.length > 0 && (
                      <button
                        onClick={() => setShowLocks(showLocks === account.id ? null : account.id)}
                        className={`flex items-center gap-2 px-4 py-2 border rounded-lg ${
                          activeLocks > 0
                            ? "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100"
                            : "border-gray-300 text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        <Shield className="w-4 h-4" />
                        Zablokowane ({activeLocks})
                      </button>
                    )}
                  </div>

                  {showLogs === account.id && accountLogs.length > 0 && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-900">Ostatnie wywołania webhooka</h4>
                        {account.platform === "instagram_direct" && account.linked_instagram_business_id && (
                          <span className="text-xs text-gray-500">
                            (łącznie z Instagram Business)
                          </span>
                        )}
                      </div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {accountLogs.map((log) => {
                          const isSuccess = log.status === "success";
                          const isPermanentFailure = log.status === "permanent_failure";
                          const bgColor = isSuccess
                            ? "bg-green-50 border border-green-200"
                            : isPermanentFailure
                            ? "bg-orange-50 border border-orange-200"
                            : "bg-red-50 border border-red-200";
                          const statusBadge = isSuccess
                            ? "bg-green-100 text-green-800"
                            : isPermanentFailure
                            ? "bg-orange-100 text-orange-800"
                            : "bg-red-100 text-red-800";
                          const statusLabel = isSuccess
                            ? "Sukces"
                            : isPermanentFailure
                            ? "Brak zgody"
                            : "Błąd";

                          return (
                            <div key={log.id} className={`p-3 rounded-lg text-sm ${bgColor}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    <span className="font-medium text-gray-900">{log.event_type}</span>
                                    {log.external_username && (
                                      <span className="text-gray-600">• {log.external_username}</span>
                                    )}
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusBadge}`}>
                                      {statusLabel}
                                    </span>
                                    {log.http_code && (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                        HTTP {log.http_code}
                                      </span>
                                    )}
                                  </div>
                                  {log.error_message && (
                                    <p className={`text-xs mt-1 ${isPermanentFailure ? 'text-orange-700' : 'text-red-700'}`}>
                                      {log.error_message}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500 mt-1">
                                    {new Date(log.created_at).toLocaleString("pl-PL")}
                                  </p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {showResponses === account.id && responses[account.id]?.length > 0 && (
                    <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-1">Historia Interakcji i Odpowiedzi AI</h4>
                          <p className="text-xs text-gray-600">
                            {account.platform === "instagram_direct" && account.linked_instagram_business_id
                              ? "Kompletna historia triggerów i odpowiedzi (łącznie z Instagram Business)"
                              : "Kompletna historia triggerów i odpowiedzi"}
                          </p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <div>Sukces: {responses[account.id].filter(r => r.has_response).length}</div>
                          <div>Brak AI: {responses[account.id].filter(r => !r.has_response && r.trigger_status === 'success').length}</div>
                        </div>
                      </div>
                      <div className="space-y-3 max-h-96 overflow-y-auto">
                        {responses[account.id].map((item, index) => {
                          const hasResponse = item.has_response && item.ai_response_text;
                          const isDelivered = item.delivery_status === "delivered" || item.delivery_status === "sent";
                          const isFailed = item.delivery_status === "failed";
                          const triggerSuccess = item.trigger_status === "success";

                          return (
                            <div
                              key={item.response_id || item.log_id || index}
                              className={`p-4 rounded-lg border ${
                                hasResponse && isDelivered
                                  ? "bg-white border-green-300"
                                  : hasResponse && isFailed
                                  ? "bg-red-50 border-red-300"
                                  : hasResponse
                                  ? "bg-gray-50 border-gray-300"
                                  : "bg-yellow-50 border-yellow-300"
                              }`}
                            >
                              <div className="space-y-2">
                                {/* Header */}
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-medium text-gray-900">
                                      {item.interaction_type}
                                    </span>
                                    {item.external_username && (
                                      <span className="text-sm text-gray-600">→ {item.external_username}</span>
                                    )}

                                    {/* Status badges */}
                                    {hasResponse ? (
                                      <span
                                        className={`px-2 py-0.5 rounded text-xs font-medium ${
                                          isDelivered
                                            ? "bg-green-100 text-green-800"
                                            : isFailed
                                            ? "bg-red-100 text-red-800"
                                            : "bg-gray-100 text-gray-800"
                                        }`}
                                      >
                                        {item.delivery_status === "sent" && "✓ Wysłano"}
                                        {item.delivery_status === "delivered" && "✓ Dostarczono"}
                                        {item.delivery_status === "failed" && "✗ Błąd wysyłki"}
                                        {item.delivery_status === "pending" && "⏳ Oczekuje"}
                                      </span>
                                    ) : (
                                      <span className="px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                        ⚠ Brak odpowiedzi AI
                                      </span>
                                    )}

                                    {/* Trigger status */}
                                    {item.trigger_http_code && (
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        triggerSuccess ? "bg-blue-100 text-blue-800" : "bg-orange-100 text-orange-800"
                                      }`}>
                                        Webhook {item.trigger_http_code}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-xs text-gray-500 whitespace-nowrap">
                                    {new Date(item.response_sent_at || item.trigger_created_at).toLocaleString("pl-PL")}
                                  </span>
                                </div>

                                {/* AI Response Text */}
                                {hasResponse && (
                                  <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-sm text-gray-900 whitespace-pre-wrap">{item.ai_response_text}</p>
                                  </div>
                                )}

                                {/* No response warning */}
                                {!hasResponse && triggerSuccess && (
                                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                                    <p className="text-sm text-yellow-900 font-medium mb-1">
                                      N8N nie odesłało odpowiedzi przez callback
                                    </p>
                                    <p className="text-xs text-yellow-700">
                                      Webhook został wywołany pomyślnie (HTTP {item.trigger_http_code}), ale system nie otrzymał wygenerowanej odpowiedzi AI.
                                      Sprawdź czy N8N wysyła callback do: <code className="bg-yellow-100 px-1 rounded">/interaction-response-callback</code>
                                    </p>
                                  </div>
                                )}

                                {/* Debug info */}
                                <details className="text-xs">
                                  <summary className="cursor-pointer text-gray-500 hover:text-gray-700 select-none">
                                    Debug info
                                  </summary>
                                  <div className="mt-2 p-2 bg-gray-100 rounded space-y-1 font-mono">
                                    <div><strong>User ID:</strong> {item.external_user_id}</div>
                                    {item.trigger_webhook_url && (
                                      <div><strong>Webhook:</strong> {item.trigger_webhook_url.substring(0, 50)}...</div>
                                    )}
                                    {item.meta_message_id && (
                                      <div><strong>Message ID:</strong> {item.meta_message_id}</div>
                                    )}
                                    {item.trigger_created_at && (
                                      <div><strong>Trigger:</strong> {new Date(item.trigger_created_at).toLocaleString("pl-PL")}</div>
                                    )}
                                    {item.response_created_at && (
                                      <div><strong>Response:</strong> {new Date(item.response_created_at).toLocaleString("pl-PL")}</div>
                                    )}
                                  </div>
                                </details>

                                {/* Error Messages */}
                                {item.response_error_message && (
                                  <div className="p-2 bg-red-100 border border-red-300 rounded">
                                    <p className="text-xs font-medium text-red-900 mb-1">Błąd wysyłki DM:</p>
                                    <p className="text-xs text-red-700">{item.response_error_message}</p>
                                  </div>
                                )}
                                {item.trigger_error_message && !triggerSuccess && (
                                  <div className="p-2 bg-orange-100 border border-orange-300 rounded">
                                    <p className="text-xs font-medium text-orange-900 mb-1">Błąd webhook:</p>
                                    <p className="text-xs text-orange-700">{item.trigger_error_message}</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* 🔒 Locked Conversations Panel */}
                  {showLocks === account.id && accountLocks.length > 0 && (
                    <div className="mt-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                            <Shield className="w-4 h-4 text-orange-600" />
                            Zablokowane konwersacje
                          </h4>
                          <p className="text-xs text-gray-600">
                            Gdy ktoś z zespołu ręcznie odpowie w konwersacji, bot automatycznie przestaje wysyłać wiadomości do tej osoby.
                          </p>
                        </div>
                        <div className="text-right text-xs text-gray-500">
                          <div>Aktywne: <span className="font-medium text-orange-700">{activeLocks}</span></div>
                          <div>Łącznie: {accountLocks.length}</div>
                        </div>
                      </div>
                      <div className="space-y-2 max-h-96 overflow-y-auto">
                        {accountLocks.map((lock) => {
                          const isActive = lock.locked;
                          return (
                            <div
                              key={lock.id}
                              className={`p-3 rounded-lg border ${
                                isActive
                                  ? "bg-white border-orange-300"
                                  : "bg-green-50 border-green-200 opacity-75"
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                                    {isActive ? (
                                      <Lock className="w-4 h-4 text-orange-600 flex-shrink-0" />
                                    ) : (
                                      <Unlock className="w-4 h-4 text-green-600 flex-shrink-0" />
                                    )}
                                    <span className="text-sm font-medium text-gray-900">
                                      {lock.external_user_id}
                                    </span>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                      isActive
                                        ? "bg-orange-100 text-orange-800"
                                        : "bg-green-100 text-green-800"
                                    }`}>
                                      {isActive ? "🔒 Zablokowana" : "✓ Odblokowana"}
                                    </span>
                                    <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                                      {lock.lock_source === "webhook_handler"
                                        ? "Auto-wykrycie"
                                        : lock.lock_source === "manual_reply"
                                        ? "Ręczna odpowiedź"
                                        : lock.lock_source === "admin_action"
                                        ? "Administrator"
                                        : lock.lock_source}
                                    </span>
                                  </div>
                                  {lock.locked_by_name && (
                                    <p className="text-xs text-gray-600 mb-1">
                                      Zablokował: <span className="font-medium">{lock.locked_by_name}</span>
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    {isActive ? "Zablokowano: " : "Odblokowano: "}
                                    {new Date(isActive ? lock.locked_at : (lock.unlocked_at || lock.locked_at)).toLocaleString("pl-PL")}
                                  </p>
                                </div>
                                {isActive && (
                                  <button
                                    onClick={() => unlockConversation(account.id, lock.external_user_id)}
                                    disabled={unlocking === lock.external_user_id}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                    title="Odblokuj – bot będzie mógł ponownie odpowiadać"
                                  >
                                    {unlocking === lock.external_user_id ? (
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                    ) : (
                                      <Unlock className="w-3 h-3" />
                                    )}
                                    Odblokuj
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
